import { memoize, merge } from 'lodash-es';
import { ErrorResponse, Log, User, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';

import { AuthManagerConfig, LogLevel } from 'types';
import { convertStrToLogLevel, getMainConfig, logger } from 'utils';
import { cordovaOidcSettings, isInAppBrowserAvailable } from 'utils/cordova';

import mockUserData from './mockUser.json';

/**
 * This is the default settings of [UserManagerSettings.accessTokenExpiringNotificationTimeInSeconds](https://authts.github.io/oidc-client-ts/interfaces/UserManagerSettings.html#accesstokenexpiringnotificationtimeinseconds)
 *
 * This setting is used to notify the application when the access token is about to expire.
 * The default value is 60 seconds.
 *
 * The sessings can be changed by overriding the `oidc.accessTokenExpiringNotificationTimeInSeconds` property in the main configuration.
 */
export const ACCESS_TOKEN_EXPIRING_NOTIFICATION_TIME_IN_SECONDS = 60;

const ONE_WEEK_INTERVAL = 7 * 24 * 60 * 60 * 1000;

export const getOidcSettings = () => {
  if (isInAppBrowserAvailable()) {
    return cordovaOidcSettings;
  }
  const { origin, href, pathname } = window.location;
  // use httpsOrigin because of app scheme not working as redirect url on RH-SSO.
  const redirectUri = `${origin}${pathname.replace(/[^/]+$/, '')}signincallback.html`;
  // use the original app scheme url when logout in devices
  const withoutHashUri = `${href.replace(/#.*/, '')}`;
  return {
    redirect_uri: href,
    silent_redirect_uri: redirectUri,
    popup_redirect_uri: redirectUri,
    post_logout_redirect_uri: withoutHashUri,
  };
};

export const getMockUser = memoize((oidcConfig: AuthManagerConfig | null = null) => {
  const mockUser = (oidcConfig === null && getMainConfig('mockUser')) || oidcConfig?.mockUser;
  if (!mockUser) {
    return null;
  }

  const { roles, rights, preferred_username: preferredUsername, ...mockProfile } = mockUser;

  return new User({
    ...mockUserData,
    profile: {
      ...mockUserData.profile,
      roles: roles || [],
      rights: rights || [],
      preferred_username: preferredUsername || 'user name',
      ...mockProfile,
    },
    expires_at: Date.now() + ONE_WEEK_INTERVAL,
  });
});

const oidcParams = ['state', 'session_state', 'id_token', 'code', 'access_token', 'token_type', 'expires_in', 'iss'];

/**
 * Removes specified query parameters from the query part of a hash URL.
 *
 * @param hash The hash string (e.g. "#/route?foo=1&bar=2")
 * @param paramsToRemove Array of parameter names to remove from the query string
 * @returns The hash string with specified parameters removed from the query part
 */
export function removeParamsFromHashQuery(hash: string, paramsToRemove: string[]): string {
  const hashStripped = hash.replace(/^#/, '');
  const [, path, divider, query] = hashStripped.match(/(.*?)([?&])(.+)/) || [];

  const searchParams = new URLSearchParams(query ?? path);
  paramsToRemove.forEach((param) => searchParams.delete(param));
  const newQuery = searchParams.toString();

  return newQuery ? `#${path}${divider}${newQuery}` : `#${path}`;
}
export const removeOidcParams = (url: string) => removeParamsFromHashQuery(url, oidcParams);

export const shouldCallRedirectCallback = (hash: string) => Boolean(hash.match(/([?&#])state=[^&]/));

const logLevelMap: Record<LogLevel, Log> = {
  [LogLevel.WARNING]: Log.WARN,
  [LogLevel.INFO]: Log.INFO,
  [LogLevel.DEBUG]: Log.DEBUG,
  [LogLevel.ERROR]: Log.ERROR,
};

export const setupOidcLogLevel = (configLogLevel?: LogLevel) => {
  const logLevelStr = (configLogLevel || null) as string | null;
  const logLevel = convertStrToLogLevel(logLevelStr);
  const log = (logLevel && logLevelMap[logLevel]) || Log.NONE;
  Log.setLogger(logger);
  Log.setLevel(log);
};

export const convertConfigToUserManagerSettings = (customConfig: AuthManagerConfig): UserManagerSettings => {
  const baseConfig = merge({} as AuthManagerConfig, getMainConfig('oidc'), customConfig);
  const { clientId, logLevel, allowAutoLogin, client_id: oidcClientId, mockUser, ...oidcConfig } = baseConfig;

  const redirectUrls = getOidcSettings();

  return {
    client_id: oidcClientId || clientId,
    response_mode: 'fragment',
    scope: 'openid',
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: ACCESS_TOKEN_EXPIRING_NOTIFICATION_TIME_IN_SECONDS,
    loadUserInfo: true,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    ...redirectUrls,
    ...oidcConfig,
  };
};

export const getError = (error: ErrorResponse) => `${error.message}${error.error ? ` (${error.error})` : ''}`;
